/* eslint-disable */
import * as yup from 'yup'
import { BaseSchema, ValidationError } from 'yup'
import set from 'lodash/set'
import { ValidateOptions } from 'yup/lib/types'
import { toArray } from './utils'

type Fields = Record<string, BaseSchema>

export const symbolOptions = Symbol('options')

// @ts-ignore
window.yup = yup

export class Serializer<T, R = T, D = T> {
  constructor(
    record?: R,
    options: any = {},
    many = false
  ) {
    this.$options = options
    this._isPrimitive = (this.constructor as any).$isPrimitive || false
    this._many = many
    this.$fill(record)
    this._createSchema()
  }

  public static $isPrimitive = false

  public static getFields(): BaseSchema | Fields {
    return Reflect.getMetadata('fields', this.prototype) as Fields
  }

  public static createSchema (fields = this.getFields(), many = false) {
    const schema = this.$isPrimitive
      ? fields as BaseSchema
      : yup.object().shape(fields as Fields)

    return many ? yup.array().of(schema) : schema
  }

  public $record?: Readonly<R>

  public $options: any = null

  private _data: any

  private readonly _isPrimitive: boolean

  private readonly _many: boolean

  private _schema!: BaseSchema

  private _error: ValidationError | null = null

  private _errors: Record<string, string> = {}

  private _cachedFields: Fields | null = null

  private _innerSerializers: Record<string, Serializer<any>> = {}

  private _getFields(): BaseSchema | Fields {
    if (this._cachedFields) {
      return this._cachedFields
    } else {
      const fields = (this.constructor as typeof Serializer).getFields()

      if (!fields) {
        throw new Error(`No fields provided for serializer: ${this.constructor.name}`)
      }

      return this._cachedFields = fields as any
    }
  }

  private _setError(error: ValidationError) {
    this._error = error

    if (error) {
      if (error.inner) {
        this._errors = error.inner.reduce(
          (acc, error) =>
            error.path
              ? set(acc, error.path, error.message)
              : acc
          ,{})
      } else {
        this._errors = error.path ? {
          [error.path]: error.message
        } : {}
      }
    }
  }

  private _createSchema() {
    const fields = this._getFields()

    this._schema = (this.constructor as typeof Serializer)
      .createSchema(fields, this._many)
  }

  private _createSchemaWithKeys(keys: string[]) {
    const fields = this._getFields()

    const schemaFields = keys.reduce((acc, key) => ({
      ...acc,
      [key]: fields[key]
    }), {})

    return yup.object().shape(schemaFields)
  }

  private _innerSerializeField(field: BaseSchema, value: any, key: string) {
    const isNullable = field.spec.nullable

    if (value === void 0 || (!isNullable && value === null)) {
      const defaultValue = field.getDefault()

      if (defaultValue !== void 0) {
        if (Array.isArray(defaultValue)) {
          value = defaultValue[defaultValue.length - 1]
        } else {
          value = defaultValue
        }
      } else {
        value = null
      }
    }

    // @ts-ignore
    if (field.spec.serializer) {
      // Bypass serializer if value is null and field is nullable
      if (isNullable && value === null) {
        return value
      }

      // @ts-ignore
      const serializerClass = field.spec.serializerClass

      if (!serializerClass) {
        throw new TypeError('serializer class not found')
      }

      // @ts-ignore
      const isMany = field.spec.many || false

      // @ts-ignore
      const instance = new serializerClass(value, field.spec.options, isMany)

      this._innerSerializers[key] = instance

      if (serializerClass.$isPrimitive || isMany) {
        return instance.$toPrimitive()
      }

      return instance
    }

    return value
  }

  protected _serializeField(data: any) {
    const fields = this._getFields()

    const payload = new Map()

    for (const [key, field] of Object.entries(fields as Fields)) {
      let value: any = void 0

      if (data != null) {
        // @ts-ignore
        value = data[key]
      }

      payload.set(key, this._innerSerializeField(field, value, key))
    }

    return Object.fromEntries(payload)
  }

  protected _serializeMany(data: any) {
    return toArray<R>(data).map(
      (value) => {
        const instance = new (this.constructor as any)(value, this.$options)
        if (this._isPrimitive) return instance.$toPrimitive()
        return instance
      }
    ) as T[]
  }

  private _serialize(rawData?: R) {
    if (this._many) {
      return this.$setData(
        this._serializeMany(rawData)
      )
    } else {
      const data = rawData ? this.serialize(rawData) : rawData

      return this.$setData(
        this._serializeField(
          data
        )
      )
    }
  }

  private _innerDeserializeField(field: BaseSchema, value: any) {
    // @ts-ignore
    if (field.spec.serializer) {
      // Bypass serializer if value is null and field is nullable
      // @ts-ignore
      const serializerClass = field.spec.serializerClass

      if (!serializerClass) {
        throw new TypeError('serializer class not found')
      }

      let instance: Serializer<any>

      if (value instanceof serializerClass) {
        instance = value
      } else {
        // @ts-ignore
        instance = new serializerClass(void 0 as any, field.spec.options, field.spec.many || false)
        instance.$setData(value)
      }

      return instance.$toJSON()
    }

    return value
  }

  protected _deserializeField(data: any) {
    if (this._isPrimitive) {
      return this.deserialize(data)
    }

    const fields = this._getFields()

    const payload = new Map()

    for (const [key, field] of Object.entries(fields)) {
      payload.set(key, this._innerDeserializeField(field, data[key]))
    }

    return this.deserialize(
      Object.fromEntries(payload)
    )
  }

  protected _deserializeMany(data: any) {
    return toArray(data).map(value => this._deserializeField(value))
  }

  private _deserialize() {
    const data = this.$getData()

    if (this._many) {
      return this._deserializeMany(data)
    } else {
      return this._deserializeField(data)
    }
  }

  private _toPrimitive() {
    return this.$getData()
  }

  protected _defineAccessors() {
    const value = this._data

    Object.keys(value).forEach(key => {
      if (!(key in this)) {
        Object.defineProperty(this, key, {
          get(): any {
            return this._data[key]
          },
          set(v: any) {
            this.$clearErrorForKey(key)
            this._data[key] = v
          }
        })
      }
    })
  }

  public $fill(record?: R) {
    const fields = this._getFields()

    if (record !== void 0) {
      this.$record = Object.freeze(record)
    }

    this.$clearErrors()

    if (this._isPrimitive) {
      this._data = this.serialize(
        record === void 0
          ? (fields as BaseSchema).getDefault() as any
          : record
      )
      return
    }

    this._serialize(record)
  }

  $getData() {
    return this._data
  }

  $setData(value: T | T[]) {
    if (this._isPrimitive || this._many) {
      this._data = value
    } else {
      this._data = value
      this._defineAccessors()
    }
  }

  $toPrimitive() {
    return this._toPrimitive()
  }

  $toJSON() {
    return this._deserialize()
  }

  toJSON() {
    return this.$toJSON()
  }

  $clearErrorForKey(key: string) {
    if (key in this._errors) {
      delete this._errors[key]
    }
  }

  $clearErrors() {
    this._error = null
    this._errors = {}
  }

  get $errors(): Record<keyof T, string> {
    return this._errors as any
  }

  get $hasError(): Record<keyof T, boolean> {
    return Object.keys(this._errors).reduce((acc, key) => ({
      ...acc,
      [key]: key in this._errors
    }), {}) as any
  }

  private _validate(keys: string[] | null, options: ValidateOptions = {}) {
    const schema = keys === null
      ? this._schema
      : this._createSchemaWithKeys(keys)

    const deserializedData = this.$toJSON()

    return schema.validate(deserializedData, {
      abortEarly: false,
      ...options,
      context: this
    }).then(
      value => Promise.resolve(value),
      reason => {
        this._setError(reason)
        return Promise.reject(reason)
      }
    )
  }

  $validate(options: ValidateOptions = {}) {
    const keys = this.validateKeys()

    return this._validate(keys, options)
  }

  $validateKeys(keys: string[], options: ValidateOptions = {}) {
    return this._validate(keys, options)
  }

  protected serialize(data: R): T {
    return data as any
  }

  protected deserialize(data: T): D {
    return data as any
  }

  protected validateKeys(): string[] | null {
    return null
  }
}

